import proDocumentsService from '../../../services/proDocuments.service';
import type { ProDocumentUpdate } from '../../../types/proDocuments';

/**
 * A document attached to an order that is still being created — no
 * pronumber/fileinfo row exists yet, so the file and its metadata live only
 * in client state until `replayStagedDocs` runs after the order create
 * commits (design doc §6).
 */
export interface StagedDoc {
  /** Negative, decreasing — see `nextTempId`. Stands in for `map_recno`. */
  tempId: number;
  file: File;
  documenttype: string;
  description: string;
  /** '' — no drivers exist pre-create (order has no persisted pro yet). */
  driver: string;
  forinvoice: 'Y' | 'N';
}

// Monotonic counter for staged-doc temp ids. Seeded from -Date.now() so ids
// are already negative and never collide with a real map_recno, mirroring
// the pending-new-leg convention (useOrderEditing.ts nextTempLegrecno).
let nextTempIdCounter = -Date.now();

export function nextTempId(): number {
  nextTempIdCounter -= 1;
  return nextTempIdCounter;
}

/**
 * Replays staged doc uploads against a just-created order, one at a time
 * (sequential — mirrors V1's per-file behavior and keeps upload order
 * predictable for the caller's progress UI). Never throws: every failure is
 * collected so the caller can toast the failed filenames. The order itself is
 * already created by the time this runs, so a failed upload here never rolls
 * anything back — the user re-uploads from the Docs tab afterward.
 */
export async function replayStagedDocs(
  // A template's allocated pronumber is a "T…" string; pro_documents routes it to
  // templatecms_map off that same prefix, so the replay itself is unchanged.
  pronumber: number | string,
  docs: StagedDoc[],
  upload: typeof proDocumentsService.upload = proDocumentsService.upload,
): Promise<{ failed: Array<{ name: string; error: string }> }> {
  const failed: Array<{ name: string; error: string }> = [];
  for (const doc of docs) {
    const meta: ProDocumentUpdate = {
      documenttype: doc.documenttype,
      description: doc.description,
      driver: doc.driver,
      forinvoice: doc.forinvoice,
    };
    try {
      await upload(pronumber, doc.file, meta);
    } catch (err: any) {
      failed.push({ name: doc.file.name, error: err?.message || 'Upload failed' });
    }
  }
  return { failed };
}
